/**
 * VÉRAULT Portal — Client: Payments
 * Read-only personal ledger. Three metric tiles + a clean list.
 */
import React from "react";
import { motion } from "framer-motion";
import { CreditCard } from "lucide-react";
import {
  Surface,
  PageHeader,
  EditorialRule,
  MetricTile,
  StatusTag,
  Empty,
  Skeleton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

function statusTone(p) {
  const s = (p.status || "").toLowerCase();
  if (s === "paid") return "sage";
  if (p.due_date && new Date(p.due_date) < new Date() && s !== "paid") return "terracotta";
  if (s === "pending") return "gold";
  return "neutral";
}
function statusLabel(p) {
  const s = (p.status || "pending").toLowerCase();
  if (p.due_date && new Date(p.due_date) < new Date() && s !== "paid") return "overdue";
  return s;
}

export default function ClientPayments() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    api
      .get("/client-portal/payments")
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setError("Couldn't load your payments."));
    return () => {
      alive = false;
    };
  }, []);

  const s = data?.summary;

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Payments"
          title="Your ledger."
          lede="A calm view of what's paid, what's pending, and what's due."
        />
      </motion.div>

      {error && <Empty title={error} icon={<CreditCard className="size-6" strokeWidth={1.25} />} />}

      <motion.div variants={fadeInUp}>
        <EditorialRule label="At a glance" />
        <div className="mt-6 grid grid-cols-3 gap-6">
          {s ? (
            <>
              <MetricTile label="Paid" value={s.paid} currency={s.currency} tone="accent" testid="client-pay-paid" />
              <MetricTile label="Pending" value={s.pending} currency={s.currency} tone="quiet" testid="client-pay-pending" />
              <MetricTile label="Overdue" value={s.overdue} currency={s.currency} tone={s.overdue > 0 ? "warn" : "default"} testid="client-pay-overdue" />
            </>
          ) : (
            [0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)
          )}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label="The list" />
        <div className="mt-5">
          {!data ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : !data.payments || data.payments.length === 0 ? (
            <Empty
              title="No payments scheduled yet"
              hint="Once your experience is invoiced, the schedule will appear here."
              icon={<CreditCard className="size-6" strokeWidth={1.25} />}
            />
          ) : (
            <Surface variant="default" padded={false} className="overflow-hidden">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                {data.payments.map((p) => (
                  <li key={p.id}>
                    <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_120px_120px_120px] items-center gap-3 sm:gap-6 px-5 sm:px-6 py-4" data-testid={`client-payment-${p.id}`}>
                      <div className="min-w-0">
                        <p className="font-body text-[14px] text-[hsl(var(--foreground))] truncate">
                          {p.description || "Payment"}
                        </p>
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                          {p.payment_type || "scheduled"}
                        </p>
                      </div>
                      <div className="hidden sm:block font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                        Due {p.due_date ? fmt.formatDate(p.due_date) : "—"}
                      </div>
                      <div className="hidden sm:block">
                        <StatusTag tone={statusTone(p)}>{statusLabel(p)}</StatusTag>
                      </div>
                      <span className="text-right font-serif text-[15px]">{fmt.money(p.amount || 0, p.currency)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Surface>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
